import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { EventType } from '@graphql/generated-base';
import { useIsNewStackEvent } from '@shared/utils/eventInfo/useIsNewStackEvent';
import { useGuestIdentificationEligibilityQuery } from './GuestIdentificationDialog.graphql.generated';

export const useGuestIdentificationDialogController = ({ skip }: { skip?: boolean } = {}) => {
  const { eventName } = useEventNameMatch();
  const isNewStackEvent = useIsNewStackEvent();

  // The provider always mounts this hook to keep the React subtree stable
  // across feature-flag / iframe / preview gate transitions; `skip` is how
  // the provider tells the controller to stay inert when those gates fail.
  const { data } = useGuestIdentificationEligibilityQuery({
    variables: { eventHandle: eventName || '' },
    batchMode: 'fast',
    ssr: false,
    skip: skip || !eventName,
  });

  const event = data?.eventByName;
  const eventType = event?.info?.eventType;
  const activeSession = event?.activeSession;
  const hasIdentity = activeSession?.hasIdentity;
  const personFullName = activeSession?.person?.fullName;
  const eventId = event?.id ?? '';
  // When the event requires guest identification, the full-site OTP gate
  // (WelcomePageOtpGate) already collects identification — the auto-opening
  // dialog and sticky CTA would be redundant. Suppress them here.
  const requiresGuestIdentification = event?.settings?.requiresGuestIdentification ?? false;

  // True when the current visitor is a candidate for email-based identification: an event on the
  // new stack with no active session identity. Whether we auto-open the dialog or show the sticky
  // "Find your invitation" button is decided downstream based on dismissal state.
  //
  // A wedding built on the new stack belongs here too. This is the new stack's identity flow, and
  // that wedding has no Firebase guest records for the legacy path to identify anyone against.
  // Audit item P7.
  const canIdentifyGuest = (eventType === EventType.general || isNewStackEvent) && !hasIdentity && !personFullName?.length && !!eventId && !requiresGuestIdentification;

  return {
    canIdentifyGuest,
    eventId,
    eventName: eventName ?? '',
  };
};
